import { ApplicabilityReasons, ApplicabilityReasonsColor, ControlScope, ScopeColor } from 'models/control';
import { NormCoverage } from 'models/dashboardScene';
import { EntityStatus, EntityStatusColor } from 'models/entityStatus';
import Tag from 'models/tag';
import { useTranslation } from 'react-i18next';
import { FluentUIPieChart, IPieChartDataPoint } from './PieChart';

interface NormCoverageProps {
  normCoverageData: NormCoverage | undefined;
  tags?: Tag[];
  height?: number;
  width?: number;
}

const NormCoverageChart = (props: NormCoverageProps) => {
  const { t } = useTranslation(['translation', 'widgets']);

  const getLink = (series: number) => {
    if (!props.normCoverageData) return;

    const normParam = `isonorm=${props.normCoverageData.isoNormId}`;

    let seriesParams = '';
    const baseParam = '/standards/overview';

    switch (series) {
      case 0: {
        seriesParams = `state=${EntityStatus.Todo}`;
        break;
      }
      case 1: {
        seriesParams = `state=${EntityStatus.Designing}`;
        break;
      }
      case 2: {
        seriesParams = `state=${EntityStatus.Implementing}`;
        break;
      }
      case 3: {
        seriesParams = `state=${EntityStatus.Implemented}`;
        break;
      }
      case 4: {
        seriesParams = `applicability=${ApplicabilityReasons.OutOfScope}`;
        break;
      }
      case 5: {
        seriesParams = 'scope=noControl';
        break;
      }
    }

    let tagStr = '';
    if (props.tags && props.tags.length > 0) {
      tagStr = `&tags=${props.tags.map((t) => t.tagId.toString()).join(',')}`;
    }

    const url = `${baseParam}?${normParam}&${seriesParams}${tagStr ? tagStr : ''}`;

    return url;
  };

  const getData = (): IPieChartDataPoint[] => {
    if (!props.normCoverageData) return [];

    const data: IPieChartDataPoint[] = [
      {
        name: t('translation:EntityStatus.Todo'),
        value: props.normCoverageData.toDoCount,
        link: getLink(0),
        color: EntityStatusColor[EntityStatus.Todo],
      },
      {
        name: t('translation:EntityStatus.Designing'),
        value: props.normCoverageData.designingCount,
        link: getLink(1),
        color: EntityStatusColor[EntityStatus.Designing],
      },
      {
        name: t('translation:EntityStatus.Implementing'),
        value: props.normCoverageData.implementingCount,
        link: getLink(2),
        color: EntityStatusColor[EntityStatus.Implementing],
      },
      {
        name: t('translation:EntityStatus.Implemented'),
        value: props.normCoverageData.implementedCount,
        link: getLink(3),
        color: EntityStatusColor[EntityStatus.Implemented],
      },
      {
        name: t('widgets:NormCoverage.OutOfScope'),
        value: props.normCoverageData.outOfScopeCount,
        link: getLink(4),
        color: ApplicabilityReasonsColor[ApplicabilityReasons.OutOfScope],
      },
      {
        name: t('widgets:NormCoverage.NotCovered'),
        value: props.normCoverageData.noControlCount,
        link: getLink(5),
        color: ScopeColor[ControlScope.NotCovered],
      },
    ];

    return data.filter((d) => d.value > 0);
  };

  //
  // Main render
  //
  return (
    <FluentUIPieChart
      height={props.height}
      width={props.width}
      data={getData()}
      label={props.normCoverageData?.controlCount.toString()}
    />
  );
};

export default NormCoverageChart;
